import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { PlusIcon, Squares2X2Icon } from '@heroicons/react/24/outline'
import { familyTreeService, type FamilyMember, type FamilyTreeOwner } from '../../services/family-tree.service'
import FamilyTreeView from './FamilyTreeView'
import AddMemberModal from './AddMemberModal'
import EditMemberModal from './EditMemberModal'

export default function FamilyTreePage() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [owner, setOwner] = useState<FamilyTreeOwner | null>(null)
  const [members, setMembers] = useState<FamilyMember[]>([])
  const [addOpen, setAddOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<FamilyMember | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const tree = await familyTreeService.getTree()
      setOwner(tree.owner)
      setMembers(tree.members)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const onEdit = useCallback((m: FamilyMember) => {
    setEditTarget(m)
  }, [])

  const onDelete = useCallback(
    async (m: FamilyMember) => {
      if (!window.confirm(t('familyTree.deleteConfirm'))) return
      try {
        await familyTreeService.deleteMember(m.id)
        toast.success(t('familyTree.delete'))
        await load()
      } catch (e: any) {
        toast.error(e?.response?.data?.message || e?.message || 'Error')
      }
    },
    [load, t],
  )

  // Refresh editTarget after a save so the modal reflects updated docs
  const onSaved = useCallback(async () => {
    await load()
    if (editTarget) {
      const fresh = (await familyTreeService.getTree()).members.find((m) => m.id === editTarget.id)
      if (fresh) setEditTarget(fresh)
    }
  }, [editTarget])

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-neutral-900">
            <Squares2X2Icon className="h-7 w-7 text-primary-600" />
            {t('familyTree.title')}
          </h1>
          <p className="mt-0.5 text-sm text-neutral-500">{t('familyTree.subtitle')}</p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4" />
          {t('familyTree.addMember')}
        </button>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-sm text-neutral-400">
          {t('common.loading') || '...'}
        </div>
      ) : members.length === 0 && owner ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-10 text-center">
          <Squares2X2Icon className="mx-auto h-10 w-10 text-neutral-300" />
          <p className="mt-3 text-sm text-neutral-500">{t('familyTree.empty')}</p>
          <button
            onClick={() => setAddOpen(true)}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <PlusIcon className="h-4 w-4" />
            {t('familyTree.addMember')}
          </button>
        </div>
      ) : owner ? (
        <FamilyTreeView owner={owner} members={members} onEdit={onEdit} onDelete={onDelete} />
      ) : null}

      <AddMemberModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={load}
        owner={owner}
        members={members}
      />
      <EditMemberModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={onSaved}
        member={editTarget}
        owner={owner}
        members={members}
      />
    </div>
  )
}
